import React, { useState, useMemo, useEffect } from "react";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useAppSubmissions } from "@/hooks/useAppSubmissions";
import { AppShowcaseCard } from "@/components/AppShowcaseCard";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { nip19 } from 'nostr-tools';
import { shuffleArray } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useAppContext } from "@/hooks/useAppContext";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { Star, Ghost, LayoutTemplate, Grid3X3, ChevronDown } from "lucide-react";

export function AppShowcase() {
  const { user } = useCurrentUser();
  const { config } = useAppContext();
  const [shouldLoad, setShouldLoad] = useState(false);

  // Delay loading submissions until after initial render
  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldLoad(true);
    }, 100); // Small delay to let the main UI render first

    return () => clearTimeout(timer);
  }, []);

  const { data: submissions = [], isLoading } = useAppSubmissions({
    enabled: shouldLoad, // Only fetch when shouldLoad is true
  });
  const [showAllApps, setShowAllApps] = useLocalStorage('shakespeare-show-more-apps', false);
  const [showFeaturedApps, setShowFeaturedApps] = useLocalStorage('shakespeare-show-featured-apps', true);
  const [showHalloweenApps, setShowHalloweenApps] = useLocalStorage('shakespeare-show-halloween-apps', true);
  const [showTemplateApps, setShowTemplateApps] = useLocalStorage('shakespeare-show-template-apps', true);

  // Get moderator hex from config
  const MODERATOR_HEX = useMemo(() => {
    try {
      const decoded = nip19.decode(config.showcaseModerator);
      return decoded.type === 'npub' ? decoded.data : '';
    } catch {
      return '';
    }
  }, [config.showcaseModerator]);
  const isModerator = user?.pubkey === MODERATOR_HEX;

  // Filter submissions without shuffling initially for better performance
  const { templateApps, halloweenApps, featuredApps, approvedApps } = useMemo(() => {
    // Filter visible submissions
    const visibleSubmissions = submissions.filter(app => !app.isHidden);

    // Separate template apps from other apps
    const nonTemplateSubmissions = visibleSubmissions.filter(app => !app.appTags.includes('Template'));

    // Filter by category (no shuffling yet - defer until rendering)
    const templates = visibleSubmissions.filter(app => app.appTags.includes('Template'));
    const featured = nonTemplateSubmissions.filter(app => app.isFeatured);
    const halloween = nonTemplateSubmissions.filter(app =>
      app.appTags.includes('Halloween Hackathon 2025') && app.isFeatured
    );
    const approved = nonTemplateSubmissions.filter(app =>
      app.isApproved && !app.isFeatured && !app.appTags.includes('Halloween Hackathon 2025')
    );

    return {
      templateApps: templates,
      halloweenApps: halloween,
      featuredApps: featured,
      approvedApps: approved,
    };
  }, [submissions]);

  // Shuffle arrays only when sections are expanded (lazy shuffling)
  const shuffledTemplateApps = useMemo(() =>
    showTemplateApps ? shuffleArray([...templateApps]) : templateApps,
    [templateApps, showTemplateApps]
  );

  const shuffledHalloweenApps = useMemo(() =>
    showHalloweenApps ? shuffleArray([...halloweenApps]) : halloweenApps,
    [halloweenApps, showHalloweenApps]
  );

  const shuffledFeaturedApps = useMemo(() =>
    showFeaturedApps ? shuffleArray([...featuredApps]) : featuredApps,
    [featuredApps, showFeaturedApps]
  );

  const shuffledApprovedApps = useMemo(() =>
    showAllApps ? shuffleArray([...approvedApps]) : approvedApps,
    [approvedApps, showAllApps]
  );

  // Don't show showcase if disabled in settings
  if (!config.showcaseEnabled) {
    return null;
  }

  // Show minimal loading state while waiting to start loading
  if (!shouldLoad) {
    return null; // Don't show anything until delayed load starts
  }

  // Show compact loading skeleton while fetching
  if (isLoading) {
    return (
      <div className="mt-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="space-y-3">
          {/* Compact Section Skeletons */}
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i} className="overflow-hidden border-border/50 bg-card shadow-sm rounded-2xl">
              <CardHeader className="p-4">
                <div className="flex items-center gap-3">
                  <Skeleton className="w-10 h-10 rounded-xl flex-shrink-0" />
                  <div className="flex-1 space-y-1.5 min-w-0">
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-3 w-48" />
                  </div>
                  <Skeleton className="w-5 h-5 flex-shrink-0" />
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Don't show anything if no submissions found
  if (!submissions.length) {
    return null;
  }

  return (
    <div className="mt-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 animate-on-load animate-fade-in-up animation-delay-700">
      {/* Apps Grid */}
      <div className="space-y-4">
        {/* Featured Apps */}
        {shuffledFeaturedApps.length > 0 && (
          <Card className="overflow-hidden border-border/50 bg-gradient-to-br from-card to-card/80 shadow-sm rounded-2xl">
            <Collapsible open={showFeaturedApps} onOpenChange={setShowFeaturedApps}>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/20">
                      <Star className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-foreground tracking-tight">Featured Apps</h3>
                      <p className="text-sm text-muted-foreground">
                        Handpicked applications showcasing the best of our community
                      </p>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform duration-200 ${showFeaturedApps ? '' : '-rotate-90'}`} />
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0">
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {shuffledFeaturedApps.slice(0, 6).map((app) => (
                      <AppShowcaseCard
                        key={app.id}
                        app={app}
                        showModerationControls={isModerator}
                        hideApprovalStatus={true}
                      />
                    ))}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        )}

        {/* Halloween Hackathon 2025 Apps */}
        {shuffledHalloweenApps.length > 0 && (
          <Card className="overflow-hidden border-border/50 bg-gradient-to-br from-card to-card/80 shadow-sm rounded-2xl">
            <Collapsible open={showHalloweenApps} onOpenChange={setShowHalloweenApps}>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/20">
                      <Ghost className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-foreground tracking-tight">Halloween Hackathon 2025</h3>
                      <p className="text-sm text-muted-foreground">
                        Featured spooky creations from our Halloween hackathon
                      </p>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform duration-200 ${showHalloweenApps ? '' : '-rotate-90'}`} />
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0">
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {shuffledHalloweenApps.slice(0, 6).map((app) => (
                      <AppShowcaseCard
                        key={app.id}
                        app={app}
                        showModerationControls={isModerator}
                        hideApprovalStatus={true}
                      />
                    ))}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        )}

        {/* Templates Section */}
        {shuffledTemplateApps.length > 0 && (
          <Card className="overflow-hidden border-border/50 bg-gradient-to-br from-card to-card/80 shadow-sm rounded-2xl">
            <Collapsible open={showTemplateApps} onOpenChange={setShowTemplateApps}>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-violet-500/20">
                      <LayoutTemplate className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-foreground tracking-tight">Templates</h3>
                      <p className="text-sm text-muted-foreground">
                        Ready-to-use templates to kickstart your project
                      </p>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform duration-200 ${showTemplateApps ? '' : '-rotate-90'}`} />
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0">
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {shuffledTemplateApps.slice(0, 6).map((app) => (
                      <AppShowcaseCard
                        key={app.id}
                        app={app}
                        showModerationControls={isModerator}
                        hideApprovalStatus={true}
                      />
                    ))}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        )}

        {/* More Apps (Approved) - Collapsible */}
        {shuffledApprovedApps.length > 0 && (
          <Card className="overflow-hidden border-border/50 bg-gradient-to-br from-card to-card/80 shadow-sm rounded-2xl">
            <Collapsible open={showAllApps} onOpenChange={setShowAllApps}>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20">
                      <Grid3X3 className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-foreground tracking-tight">
                        More Apps <span className="text-muted-foreground font-normal">({shuffledApprovedApps.length})</span>
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Explore more community apps
                      </p>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform duration-200 ${showAllApps ? '' : '-rotate-90'}`} />
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0">
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {shuffledApprovedApps.map((app) => (
                      <AppShowcaseCard
                        key={app.id}
                        app={app}
                        showModerationControls={isModerator}
                        hideApprovalStatus={true}
                      />
                    ))}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        )}
      </div>
    </div>
  );
}
